import React, { createContext, useContext, useState } from 'react';
import {
  GeneratedHistory,
  Project,
  PushHistory,
  Section,
  TestCases,
} from '@Models/types';
import { AsyncData } from '@Lib/models';
import { useAuth } from './CognitoAuthProvider';
import { testrailAPI } from '@Lib/axios/testrail';
import { testCasesAPI } from '@Lib/axios/testcases';

interface TestCaseContextProps {
  viewType: string;
  setViewType: (type: string) => void;
  panelOpen: boolean;
  setPanelOpen: (flag: boolean) => void;
  generatedHistory: GeneratedHistory[];
  fetchGeneratedHistory: () => Promise<void>;
  fetchGeneratedStatus: (id: string) => Promise<void>;
  selectedGenerated: GeneratedHistory | null;
  setSelectedGenerated: (generated: GeneratedHistory) => void;
  publishHistory: PushHistory[];
  fetchPublishHistory: () => Promise<void>;
  selectedPublish: PushHistory | null;
  setSelectedPublish: (publish: PushHistory) => void;
  generationStatus: string;
  setGenerationStatus: (status: string) => void;
  projects: Project[];
  fetchProjects: () => Promise<void>;
  selectedProject: Project | null;
  setSelectedProject: (project: Project | null) => void;
  sections: Section[];
  fetchSections: (id: number) => Promise<void>;
  selectedSection: Section | null;
  setSelectedSection: (section: Section | null) => void;
  pushToTestRail: (GTC: TestCases[]) => Promise<AsyncData>;
  genHistoryLoading: boolean;
  pushHistoryLoading: boolean;
}

const TestCaseContext = createContext<TestCaseContextProps | undefined>(
  undefined
);

export const TestCaseContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [viewType, setViewType] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('No Pending Tasks');
  const [generatedHistory, setGeneratedHistory] = useState<GeneratedHistory[]>(
    []
  );
  const [publishHistory, setPublishHistory] = useState<PushHistory[]>([]);
  const [selectedGenerated, setSelectedGenerated] =
    useState<GeneratedHistory | null>(null);
  const [selectedPublish, setSelectedPublish] = useState<PushHistory | null>(
    null
  );

  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);

  const [genHistoryLoading, setGenHistoryLoading] = useState(false);
  const [pushHistoryLoading, setPushHistoryLoading] = useState(false);

  const { logout } = useAuth();
  const testrail = testrailAPI(logout);
  const testcases = testCasesAPI(logout);

  const fetchGeneratedHistory = async () => {
    setGenHistoryLoading(true);
    const history = await testcases.getGenerationHistory();
    setGeneratedHistory(history);
    setGenHistoryLoading(false);
  };

  const fetchGeneratedStatus = async (id: string) => {
    const item = await testcases.getTestCase(id);
    if (item) {
      setSelectedGenerated(item);
    } else {
      setSelectedGenerated(null);
    }
  };

  const fetchPublishHistory = async () => {
    setPushHistoryLoading(true);
    const history = await testcases.getPushHistory();
    setPublishHistory(history);
    setPushHistoryLoading(false);
  };

  const fetchProjects = async () => {
    const projects = await testrail.getProj();
    if (projects) {
      setProjects(projects);
    } else {
      setProjects([]);
    }
  };

  const fetchSections = async (id: number) => {
    const sections = await testrail.getSections(id);
    if (sections) {
      setSections(sections);
    } else {
      setSections([]);
    }
  };

  const pushToTestRail = async (GTC: TestCases[]) => {
    if (selectedProject && selectedSection && selectedGenerated) {
      const body = {
        project_id: String(selectedProject.id),
        GTC,
        email: localStorage.getItem('email'),
        history_id: selectedGenerated.id,
        section_id: selectedSection.id,
        section_name: selectedSection.name,
      };

      const queue = (await testcases.pushToTestRailAsync(body)) as AsyncData;
      return queue;
    } else {
      throw Error('Please select a project and section to push to TestRail');
    }
  };

  return (
    <TestCaseContext.Provider
      value={{
        viewType,
        setViewType,
        panelOpen,
        setPanelOpen,
        generatedHistory,
        fetchGeneratedHistory,
        fetchGeneratedStatus,
        selectedGenerated,
        setSelectedGenerated,
        publishHistory,
        fetchPublishHistory,
        selectedPublish,
        setSelectedPublish,
        generationStatus,
        setGenerationStatus,
        projects,
        selectedProject,
        setSelectedProject,
        fetchProjects,
        sections,
        selectedSection,
        setSelectedSection,
        fetchSections,
        pushToTestRail,
        genHistoryLoading,
        pushHistoryLoading,
      }}
    >
      {children}
    </TestCaseContext.Provider>
  );
};

export const useTestCaseContext = (): TestCaseContextProps => {
  const context = useContext(TestCaseContext);
  if (!context)
    throw new Error(
      'useTestCaseContext must be used within a TestCaseContextProvider'
    );
  return context;
};
